import { useState, useRef, useEffect } from "react";
import { Status } from "@fwf/fwfa-design-system/react";
import type { ConnectionStatus, ModelInfo, HarnessState } from "@/types";
import {
  getPreviewHost,
  getLivePreviewPorts,
  getAutoPreviewPort,
  hasAnyLivePreview,
  hasMultipleLivePreviewPorts,
} from "@/lib/preview-state";
import { HarnessModelSelector } from "./HarnessModelSelector";
import { OpenProjectButton } from "./OpenProjectButton";
import { SidebarToggleIcon } from "./SidebarToggleIcon";

export type PreviewState = {
  /** Port-proxy slug URLs available for this project */
  slugs: string[];
  /** The port-proxy domain (e.g. "path26.rome2rio.com") */
  domain: string;
  /** Preferred/default preview port from the server */
  defaultPort?: number;
  /** Listening user-space ports from daemon port watcher */
  ports?: number[];
};

export type PreviewUiMenuItem = {
  id: string;
  label: string;
  detail?: string;
  badge?: string;
  tone?: "default" | "accent" | "warn";
  icon?: "play" | "check" | "refresh" | "warning" | "message" | "terminal";
};

export type PreviewUiModel = {
  visible: boolean;
  label: string;
  title: string;
  directAction?: boolean;
  triggerMode?: "direct" | "menu" | "status";
  triggerIcon?: "play" | "eye" | "check" | "warning";
  eyebrow?: string;
  tone?: "neutral" | "accent" | "success" | "warn";
  active?: boolean;
  pulse?: boolean;
  badgeCount?: number;
  menuTitle?: string;
  menuDescription?: string;
  menuItems?: PreviewUiMenuItem[];
};

interface Props {
  agentId: string | null;
  status: ConnectionStatus;
  workingDirectory: string | null;
  availableModels: ModelInfo[];
  currentModelId: string | null;
  isRefreshingModels: boolean;
  harnessStates: HarnessState[];
  onChangeAgent: (agentId: string) => void;
  onChangeModel: (modelId: string) => void;
  onRefreshModels: () => void;
  onLogout: () => void;
  onReconnect: () => void;
  onDebugToggle: () => void;
  debugVisible: boolean;
  showOpenProjectButton?: boolean;
  onMenuToggle?: () => void;
  menuOpen?: boolean;
  /** Preview panel state */
  preview?: PreviewState | null;
  previewPort?: number | null;
  previewVisible?: boolean;
  onPreviewToggle?: (port?: number) => void;
  previewUi?: PreviewUiModel | null;
  onPreviewAction?: (itemId?: string) => void;
  uiVariant?: "default" | "desktop";
}

function PreviewIcon({
  icon,
  className,
}: {
  icon: NonNullable<PreviewUiModel["triggerIcon"]> | NonNullable<PreviewUiMenuItem["icon"]>;
  className?: string;
}) {
  if (icon === "play") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
      </svg>
    );
  }

  if (icon === "eye") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      </svg>
    );
  }

  if (icon === "check") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12.75 10.125 16.125 17.25 9" />
      </svg>
    );
  }

  if (icon === "refresh") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.985 19.644v-4.992h4.992" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.93 9.17a8.25 8.25 0 0 1 13.61-3.029l2.475 2.207M19.07 14.83a8.25 8.25 0 0 1-13.61 3.029l-2.475-2.207" />
      </svg>
    );
  }

  if (icon === "warning") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4.5m0 3h.008" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.29 3.86 1.82 18a1.5 1.5 0 0 0 1.286 2.25h16.788A1.5 1.5 0 0 0 21.18 18l-8.47-14.14a1.5 1.5 0 0 0-2.42 0Z" />
      </svg>
    );
  }

  if (icon === "message") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 9.75h6.75m-6.75 3h4.5m-7.125 6.375 1.86-1.86a1.5 1.5 0 0 1 1.06-.44h8.58A2.25 2.25 0 0 0 19.5 14.58V6.75A2.25 2.25 0 0 0 17.25 4.5H6.75A2.25 2.25 0 0 0 4.5 6.75v9.75a2.25 2.25 0 0 0 2.25 2.25h.75Z" />
      </svg>
    );
  }

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h7.5m-9 3.75h10.5m-12 3.75h8.25m-4.5 3.75h4.5" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 5.25A2.25 2.25 0 0 1 6.75 3h10.5a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 17.25 21H6.75A2.25 2.25 0 0 1 4.5 18.75V5.25Z" />
    </svg>
  );
}

export function StatusBar({
  agentId,
  status,
  workingDirectory,
  availableModels,
  currentModelId,
  isRefreshingModels,
  harnessStates,
  onChangeAgent,
  onChangeModel,
  onRefreshModels,
  onLogout,
  onReconnect,
  onDebugToggle,
  debugVisible,
  showOpenProjectButton = true,
  onMenuToggle,
  menuOpen = false,
  preview,
  previewPort,
  previewVisible,
  onPreviewToggle,
  previewUi,
  onPreviewAction,
  uiVariant = "default",
}: Props) {
  const isDesktop = uiVariant === "desktop";
  const hasPreview = Boolean(preview?.slugs?.length && preview.domain);
  const activePorts = getLivePreviewPorts(preview);
  const hasMultiplePorts = hasMultipleLivePreviewPorts(preview);
  const previewHost = getPreviewHost(preview, previewPort);
  const isLive = hasPreview && (
    hasMultiplePorts
      ? hasAnyLivePreview(preview)
      : getAutoPreviewPort(preview, previewPort) !== null
  );
  const hasPreviewUi = Boolean(previewUi && onPreviewAction);
  const showPreviewButton = hasPreviewUi
    ? Boolean(previewUi?.visible)
    : Boolean(hasPreview && onPreviewToggle);
  const showConnectionBanner = status === "connecting"
    || status === "installing"
    || status === "authenticating"
    || status === "authenticating:terminal";
  const previewUiTone = previewUi?.tone || "neutral";
  // The ready state is carried by the rotating border, so it has no DS tone.
  // Everything else maps onto the design system's own status vocabulary.
  const previewStatusTone =
    previewUiTone === "warn" ? "danger" : previewUiTone === "accent" ? "info" : undefined;
  const previewUiHasMenuItems = Boolean(previewUi?.menuItems?.length);
  const previewUiTriggerMode = previewUi?.triggerMode
    ?? (previewUiHasMenuItems ? "menu" : previewUi?.directAction ? "direct" : "status");
  const previewUiTriggerIcon = previewUi?.triggerIcon || "play";
  const previewUiInteractive = hasPreviewUi && previewUiTriggerMode !== "status";
  const [portDropdownOpen, setPortDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!portDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setPortDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [portDropdownOpen]);

  useEffect(() => {
    setPortDropdownOpen(false);
  }, [previewUi?.label, previewUi?.menuTitle, showPreviewButton]);

  return (
    <div className="drag-region flex items-center justify-between px-2.5 sm:px-4 h-11 border-b border-stone-200 dark:border-white/[0.04] bg-white dark:bg-navy-dark">
      {/* Left: hamburger (mobile) + agent + model selector */}
      <div className="no-drag flex min-w-0 flex-1 items-center gap-1">
        {/* Sidebar toggle — mobile only (desktop toggle lives in the sidebar itself) */}
        {onMenuToggle && (
          <button
            onClick={onMenuToggle}
            className="md:hidden -ml-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-navy-light hover:text-gold flex-shrink-0"
            title={menuOpen ? "Hide sidebar" : "Show sidebar"}
            aria-label={menuOpen ? "Hide sidebar" : "Show sidebar"}
          >
            <SidebarToggleIcon open={menuOpen} className="h-[22px] w-[22px]" />
          </button>
        )}
        <HarnessModelSelector
          selectedAgent={agentId}
          status={status}
          availableModels={availableModels}
          currentModelId={currentModelId}
          isRefreshingModels={isRefreshingModels}
          harnessStates={harnessStates}
          onChangeAgent={onChangeAgent}
          onChangeModel={onChangeModel}
          onRefreshModels={onRefreshModels}
          uiVariant={uiVariant}
        />
      </div>

      {/* Center: reconnecting banner */}
      {showConnectionBanner && (
        <div className="no-drag flex items-center gap-1.5 text-gold">
          <svg
            className="w-3 h-3 text-gold animate-spin"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="font-body text-xs font-medium text-current">
            {status === "installing" ? "Installing..." : status === "authenticating" || status === "authenticating:terminal" ? "Authenticating..." : "Reconnecting..."}
          </span>
        </div>
      )}

      {/* Right: actions */}
      <div
        className={`no-drag flex flex-shrink-0 items-center ${
          isDesktop ? "gap-1.5" : "gap-0.5 sm:gap-1"
        } ${showConnectionBanner ? (isDesktop ? "ml-3" : "ml-2") : ""}`}
      >
        {/* Preview button — first in row; visible when a port-proxy slug is registered or a browser preview UX model is supplied */}
        {showPreviewButton && (
          <div className="relative" ref={dropdownRef}>
            {hasPreviewUi && previewUiTriggerMode === "status" ? (
              <Status
                tone={previewStatusTone}
                live={Boolean(previewUi?.pulse)}
                className={`h-7 px-1.5 text-ink-soft ${isDesktop ? "text-sm" : "text-[13px]"}`}
                title={previewUi?.title || "Preview status"}
              >
                <span className="font-body leading-none">{previewUi?.label}</span>
              </Status>
            ) : (
              <button
                onClick={() => {
                  if (hasPreviewUi) {
                    if (previewUiTriggerMode === "menu") {
                      setPortDropdownOpen(!portDropdownOpen);
                      return;
                    }
                    if (previewUi?.directAction) {
                      onPreviewAction?.();
                    }
                    return;
                  }

                  if (hasMultiplePorts) {
                    setPortDropdownOpen(!portDropdownOpen);
                  } else {
                    onPreviewToggle?.();
                  }
                }}
                className={`group relative transition-all ${
                  hasPreviewUi
                    ? `${
                        previewUiTone === "warn"
                          ? `bg-white/[0.02] text-danger ${previewUiInteractive ? "hover:bg-white/[0.04]" : ""}`
                          : previewUiTone === "success"
                            ? `bg-fwf-yellow/[0.07] text-gold ${previewUiInteractive ? "hover:bg-fwf-yellow/[0.12]" : ""}`
                            : previewUiTone === "accent"
                              ? `bg-white/[0.02] text-info ${previewUiInteractive ? "hover:bg-white/[0.04]" : ""}`
                              : `bg-white/[0.02] text-stone-100 ${previewUiInteractive ? "hover:bg-white/[0.04] hover:text-white" : ""}`
                      } rounded-full shadow-[0_12px_28px_rgba(2,12,27,0.16)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fwf-yellow/18`
                    : `focus:outline-none ${
                        previewVisible
                          ? "bg-white/[0.06] text-gold"
                          : isLive
                            ? "text-gold hover:bg-white/[0.06] hover:text-gold"
                            : "text-stone-400 hover:text-navy dark:hover:text-white hover:bg-stone-100 dark:hover:bg-white/[0.06]"
                      }`
                } ${
                  hasPreviewUi
                    ? isDesktop
                      ? `inline-flex h-7 items-center gap-2 px-3 ${previewUiInteractive ? "" : "cursor-default"}`
                      : `inline-flex h-7 items-center gap-2 px-3 ${previewUiInteractive ? "" : "cursor-default"}`
                    : isDesktop
                      ? "flex h-9 w-9 items-center justify-center"
                      : "p-1 sm:p-1.5"
                }`}
                title={hasPreviewUi
                  ? (previewUi?.title || "Preview")
                  : isLive && previewHost
                    ? `Preview — ${previewHost}`
                    : "Preview (no server detected)"}
              >
                {/* The ready state carries its own rotating gold border and no glyph;
                    every other state is indicated by its icon. */}
                {hasPreviewUi && previewUiTone === "success" && (
                  <span className="preview-glow absolute inset-[-2px] pointer-events-none rounded-full" />
                )}
                {!hasPreviewUi && isLive && !previewVisible && (
                  <span className="preview-glow absolute inset-[-2px] pointer-events-none rounded-lg" />
                )}
                {hasPreviewUi ? (
                  <>
                    {previewUiTone !== "success" && (
                      <span className={`relative inline-flex shrink-0 items-center justify-center text-current ${isDesktop ? "h-4.5 w-4.5" : "h-4 w-4"}`}>
                        <PreviewIcon icon={previewUiTriggerIcon} className={`${isDesktop ? "h-3.5 w-3.5" : "h-3 w-3"} shrink-0`} />
                      </span>
                    )}
                    <span className="relative min-w-0">
                      <span className={`block truncate font-body leading-none ${isDesktop ? "text-sm font-medium" : "text-[13px] font-medium"}`}>
                        {previewUi?.label}
                      </span>
                    </span>
                    {previewUiTriggerMode === "menu" && (
                      <svg
                        className={`${isDesktop ? "h-3 w-3" : "h-2.5 w-2.5"} shrink-0 text-current/70 transition-transform group-hover:text-current ${portDropdownOpen ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m19.5 8.25-7.5 7.5-7.5-7.5"
                        />
                      </svg>
                    )}
                  </>
                ) : (
                  <>
                    <span
                      className={`relative inline-flex shrink-0 items-center justify-center text-current ${hasPreviewUi ? (isDesktop ? "h-4.5 w-4.5" : "h-4 w-4") : ""}`}
                    >
                      <svg
                        className={`${isDesktop ? "w-4 h-4" : "w-3.5 h-3.5"} relative shrink-0`}
                        fill={isLive ? "currentColor" : "none"}
                        viewBox="0 0 24 24"
                        strokeWidth={isLive ? 0 : 1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"
                        />
                      </svg>
                    </span>
                    {/* Port count badge */}
                    {hasMultiplePorts && (
                      <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-3 h-3 rounded-full bg-gold text-navy text-[8px] font-bold leading-none">
                        {activePorts.length}
                      </span>
                    )}
                  </>
                )}
              </button>
            )}

            {hasPreviewUi && portDropdownOpen && (previewUi?.menuDescription || previewUiHasMenuItems) && (
              <div className="absolute right-0 top-full z-50 mt-2 w-[21rem] overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(180deg,rgba(17,40,53,0.98),rgba(10,28,38,0.98))] shadow-[0_24px_72px_rgba(2,12,27,0.42)] animate-scale-in origin-top-right backdrop-blur">
                <div className="border-b border-white/8 px-4 py-3">
                  {previewUi?.eyebrow && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-fwf-yellow/70">{previewUi.eyebrow}</p>
                  )}
                  <p className="mt-2 font-display text-base font-semibold text-white">{previewUi?.menuTitle || previewUi?.label}</p>
                  {previewUi?.menuDescription && (
                    <p className="mt-2 max-w-[18rem] font-body text-sm leading-6 text-stone-400">
                      {previewUi.menuDescription}
                    </p>
                  )}
                </div>
                {previewUi?.menuItems?.length ? (
                  <div className="space-y-1 p-2">
                    {previewUi.menuItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setPortDropdownOpen(false);
                          onPreviewAction?.(item.id);
                        }}
                        className="group flex w-full items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition-all hover:border-white/8 hover:bg-white/[0.05] focus-visible:outline-none focus-visible:border-white/10 focus-visible:bg-white/[0.06]"
                      >
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
                            item.tone === "warn"
                              ? "border-brand-red/16 bg-brand-red/12 text-red-100"
                              : item.tone === "accent"
                                ? "border-fwf-yellow/22 bg-fwf-yellow/12 text-gold"
                                : "border-white/10 bg-white/[0.05] text-stone-300 group-hover:text-white"
                          }`}
                        >
                          <PreviewIcon icon={item.icon || (item.tone === "warn" ? "warning" : item.tone === "accent" ? "play" : "message")} className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-body text-[15px] font-medium text-white">{item.label}</span>
                          {item.detail && (
                            <span className="mt-0.5 block font-body text-xs leading-5 text-stone-400">{item.detail}</span>
                          )}
                        </span>
                        {item.badge && (
                          <span className="rounded-full border border-fwf-yellow/30 bg-fwf-yellow/10 px-2 py-0.5 font-body text-[10px] font-semibold uppercase tracking-[0.16em] text-gold">
                            {item.badge}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-3">
                    <p className="font-body text-xs text-stone-500">No actions available yet.</p>
                  </div>
                )}
              </div>
            )}

            {/* Legacy multi-port dropdown */}
            {!hasPreviewUi && portDropdownOpen && hasMultiplePorts && (
              <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-navy-light bg-navy shadow-2xl z-50 overflow-hidden animate-scale-in origin-top-right">
                <div className="px-3 py-2 border-b border-navy-light">
                  <p className="font-mono text-xs text-stone-500 uppercase tracking-wider">Open ports</p>
                </div>
                {activePorts.map((port) => {
                  const slug = preview!.slugs[0];
                  return (
                    <button
                      key={port}
                      onClick={() => {
                        setPortDropdownOpen(false);
                        onPreviewToggle?.(port);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-navy-light transition-colors group"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${port === 3000 ? "bg-emerald-400" : "bg-fwf-yellow/60"}`} />
                      <span className="font-mono text-xs text-stone-300 group-hover:text-white truncate">
                        :{port}
                      </span>
                      <span className="ml-auto font-mono text-[11px] text-stone-500 group-hover:text-stone-400 truncate">
                        {slug}{port !== 3000 ? `-${port}` : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {showOpenProjectButton && workingDirectory && (
          <OpenProjectButton
            workingDirectory={workingDirectory}
            uiVariant={uiVariant}
          />
        )}

        <button
          onClick={onDebugToggle}
          className={`rounded-md transition-colors ${
            debugVisible
              ? "bg-white/[0.06] text-gold"
              : isDesktop
                ? "text-stone-300 hover:text-navy dark:hover:text-white hover:bg-stone-100 dark:hover:bg-navy-light"
                : "text-stone-400 hover:text-navy dark:hover:text-white hover:bg-stone-100 dark:hover:bg-navy-light"
          } ${isDesktop
            ? "flex h-9 w-9 items-center justify-center"
            : "p-1 sm:p-1.5"}`}
          title="Terminal"
        >
          <svg
            className={isDesktop ? "w-4 h-4" : "w-3.5 h-3.5"}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z"
            />
          </svg>
        </button>

        {/* Reconnect button when disconnected — always at end */}
        {status === "disconnected" && agentId && (
          <button
            onClick={onReconnect}
            className={`rounded-md font-body font-medium bg-gold text-navy hover:bg-gold-dark transition-colors active:translate-y-px ${
              isDesktop ? "px-3 py-1.5 text-sm" : "px-2.5 py-1 text-xs"
            }`}
          >
            Reconnect
          </button>
        )}

        {/* Logout/exit button — always last */}
        {(status === "ready" || status === "connecting") && (
          <button
            onClick={onLogout}
            className={`rounded-md transition-colors ${
              isDesktop
                ? "flex h-9 w-9 items-center justify-center text-stone-300 hover:text-brand-red hover:bg-stone-100 dark:hover:bg-navy-light"
                : "p-1.5 text-stone-400 hover:text-brand-red hover:bg-stone-100 dark:hover:bg-navy-light"
            }`}
            title="Disconnect agent"
          >
            <svg
              className={isDesktop ? "w-4 h-4" : "w-3.5 h-3.5"}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
